<template>
    <v-chart :options="chartOptions" auto-resize />
</template>

<script>
import ECharts from 'vue-echarts'
import _color from '@/assets/utils/color.js'
import 'echarts/lib/chart/sankey'
import 'echarts/lib/component/tooltip'

export default {
    name: 'vx-sankey-diagram',
    components: {
        'v-chart': ECharts
    },
    props: {
        series: {
            type: Array,
            default: () => []
        },
        vertical: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            color: 'secondary'
        }
    },
    computed: {
        baseColor() {
            return _color.getHexColor(this.color)
        },
        colors() {
            return _color.getAlgorithmicSet(this.baseColor, this.sources.length)
        },
        chartOptions() {
            return {
                tooltip: {
                    trigger: 'item',
                    triggerOn: 'mousemove'
                },
                series: this.localSeries
            }
        },
        localSeries() {
            return {
                type: 'sankey',
                data: this.seriesData,
                links: this.localSeriesLink,
                edgeLabel: {
                    show: true,
                    distance: 10
                },
                label: {
                    show: true,
                    formatter: function (val) {
                        return `${this.getSourcePercentage(val.data.name)}%`
                    }.bind(this)
                },
                tooltip: {
                    formatter: function (val) {
                        var displayName = `${val.name} : ${val.value}`
                        if (!val.data.source) {
                            return displayName
                        }

                        return `${displayName}  (${this.getTargetPercentage(
                            val.data.source,
                            val.data.value
                        )})%`
                    }.bind(this)
                },
                animation: true,
                nodeWidth: 20,
                nodeGap: 8,
                nodeAlign: 'left',
                orient: this.vertical ? 'vertical' : 'horizontal',
                draggable: false
            }
        },
        seriesLink() {
            if (!this.series) {
                return []
            }
            return this.series.map((link) => {
                return {
                    ...link,
                    source: link.source.toTitleCase(),
                    target: link.target.toTitleCase()
                }
            })
        },
        sources() {
            if (!this.seriesLink) {
                return []
            }
            const uniqueNames = new Set()
            const sources = []

            this.seriesLink.forEach((series) => {
                if (!uniqueNames.has(series.source)) {
                    sources.push(series.source)
                    uniqueNames.add(series.source)
                }
                if (!uniqueNames.has(series.target)) {
                    sources.push(series.target)
                    uniqueNames.add(series.target)
                }
            })

            return sources
        },
        seriesData() {
            return this.sources.map((source, index) => {
                return {
                    name: source,
                    value: this.setSourceValue(source),
                    itemStyle: {
                        color: this.colors[index],
                        borderColor: this.colors[index]
                    }
                }
            })
        },
        localSeriesLink() {
            if (!this.seriesLink) {
                return []
            }
            return this.seriesLink.map((link) => {
                return {
                    ...link,
                    lineStyle: this.addLineStyle(link.source)
                }
            })
        }
    },
    methods: {
        setSourceValue(name) {
            return this.findSourceLinkByName(name).reduce(
                (total, obj) => total + Number(obj.value),
                0
            )
        },
        findSourceDataByName(name) {
            return this.seriesData.find((item) => item.name == name)
        },
        findSourceLinkByName(name) {
            return this.seriesLink.where((item) => item.source == name)
        },
        findTargetLinkByName(name) {
            return this.seriesLink.find((item) => item.target == name)
        },
        addLineStyle(name) {
            var sourceItem = this.findSourceDataByName(name)
            if (!sourceItem) {
                return null
            }

            return {
                color: sourceItem.itemStyle.color,
                opacity: 0.4
            }
        },
        getSourceTotal(name) {
            var source = this.findSourceDataByName(name)
            if (!source) {
                return 0
            }
            return source.value
        },
        getSourcePercentage(name) {
            var target = this.findTargetLinkByName(name)
            var sourceTotal = this.getSourceTotal(target && target.source)
            if (!target) {
                // if the name is the first source
                return (100).toFixed(2)
            }
            var targetTotal = target && target.value
            return ((targetTotal / sourceTotal) * 100).toFixed(2)
        },
        getTargetPercentage(name, targetTotal) {
            var sourceTotal = this.getSourceTotal(name)

            return ((targetTotal / sourceTotal) * 100).toFixed(2)
        }
    },
    mounted() {}
}
</script>
